import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {FieldBlock, FormBlock, GroupBlock, ListBlock, StaticTextBlock} from '../../../shared/models/form-schema.model';
import {FieldRendererComponent} from '../field-renderer/field-renderer.component';
import {GroupBlockComponent} from '../group-block/group-block.component';
import {ListBlockComponent} from '../list-block/list-block.component';
import {StaticTextComponent} from '../static-text/static-text.component';
import {RulesEngineService} from '../../../shared/services/rules-engine.service';
import {blockPathSegment} from '../../../shared/services/visibility-evaluator';
import {FormVisibilityState} from '../../services/form-visibility-state.service';
import {Subscription} from 'rxjs';

/**
 * Renders one block of a form definition and decides whether it is on screen at all.
 *
 * **Since #470 the visibility decision is not made here.** {@link DynamicFormComponent} evaluates the *whole*
 * definition — including the pages that are not currently in the DOM — and publishes one verdict through
 * {@link FormVisibilityState}; this component only reads its own entry out of it. That is what makes "what the
 * employee sees" and "what the form validates and submits" structurally incapable of disagreeing, and it is why
 * a required field on a page the employee has already left can no longer keep the form invisibly invalid.
 *
 * Outside a `DynamicFormComponent` (a Storybook story, an isolated spec) no verdict is published, so the
 * component falls back to evaluating its own block — there is nothing to disagree with in that case.
 */
@Component({
  selector: 'forms-block-renderer',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FieldRendererComponent,
    forwardRef(() => GroupBlockComponent),
    forwardRef(() => ListBlockComponent),
    StaticTextComponent
  ],
  styles: [':host { display: block; }'],
  template: `
    @if (isVisible) {
      <div class="block-renderer-wrapper">
        @switch (block.type) {
          @case ('group') {
            @if (getFormGroup(block.key); as group) {
              <forms-group-block [block]="asGroup(block)" [group]="group"></forms-group-block>
            }
          }
          @case ('list') {
            @if (getFormArray(block.key); as array) {
              <forms-list-block [block]="asList(block)" [array]="array"></forms-list-block>
            }
          }
          @case ('static-text') {
            <forms-static-text [block]="asStaticText(block)"></forms-static-text>
          }
          @default {
            @if (getFormControl(block.key); as control) {
              <forms-field-renderer [block]="asField(block)" [control]="control"></forms-field-renderer>
            }
          }
        }
      </div>
    }
  `
})
export class BlockRendererComponent implements OnInit, OnChanges, OnDestroy {
  @Input() block!: FormBlock;
  @Input() parentForm!: FormGroup;

  isVisible = true;

  /**
   * The form-wide verdict, when this renderer runs inside a {@link DynamicFormComponent}. Optional on purpose:
   * a story or an isolated spec renders a block with no form around it, and must still work.
   */
  private readonly visibility = inject(FormVisibilityState, {optional: true});
  private subs = new Subscription();
  private initialized = false;

  constructor(private rulesEngine: RulesEngineService, private cdr: ChangeDetectorRef) {
  }

  get isFieldBlock(): boolean {
    return this.block.type !== 'group' && this.block.type !== 'list' && this.block.type !== 'static-text';
  }

  ngOnInit() {
    this.wire();
    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    // The runtime form is rebuilt whenever the definition changes (e.g. after a block edit is saved).
    // Block-renderer instances are reused (tracked by id), so re-wire against the new form/block here —
    // otherwise newly-configured conditional logic would not apply until the component is recreated.
    if (this.initialized && (changes['parentForm'] || changes['block'])) {
      this.wire();
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  asField(block: FormBlock): FieldBlock {
    return block as FieldBlock;
  }

  asGroup(block: FormBlock): GroupBlock {
    return block as GroupBlock;
  }

  asList(block: FormBlock): ListBlock {
    return block as ListBlock;
  }

  asStaticText(block: FormBlock): StaticTextBlock {
    return block as StaticTextBlock;
  }

  /**
   * The control backing this block, or `null` when the definition names a key the form has no control for.
   *
   * Always the **array** lookup: `AbstractControl.get('a.b')` splits its argument on `.`, so an author-supplied
   * key containing a dot would resolve to `null` and be handed to `[formControl]`, which throws inside change
   * detection and takes the whole form down (#470).
   */
  getFormControl(key: string): FormControl | null {
    return this.parentForm.get([key]) as FormControl | null;
  }

  getFormGroup(key: string): FormGroup | null {
    return this.parentForm.get([key]) as FormGroup | null;
  }

  getFormArray(key: string): FormArray | null {
    return this.parentForm.get([key]) as FormArray | null;
  }

  /** Evaluate rules and (re-)subscribe to the current root form's value changes. */
  private wire() {
    this.subs.unsubscribe();
    this.subs = new Subscription();

    this.evaluateRules();

    // Sync a sensible default for value-bearing fields that haven't been initialised yet.
    if ('key' in this.block && this.block.type !== 'group' && this.block.type !== 'list') {
      const control = this.parentForm.get([this.block.key]);
      if (control && (control.value === undefined || control.value === null)) {
        const defaultValue = (this.block as any).type === 'boolean' ? false : '';
        control.setValue(defaultValue, {emitEvent: false});
      }
    }

    // Listen on the finalised root form for any value change so dependent rules re-evaluate. The form's own
    // subscription is registered first (in buildForm), so by the time this handler runs the verdict is current.
    setTimeout(() => {
      const root = this.parentForm.root as FormGroup;
      this.subs.add(root.valueChanges.subscribe(() => this.evaluateRules()));
      this.evaluateRules();
    });
  }

  /**
   * The path of this block's control from the root form, as **verbatim** segments (never a dotted string).
   *
   * Built by walking up from {@link parentForm}: each `FormGroup` hop contributes its control name, each
   * `FormArray` hop its row index — which is what lets a repeating row's block find its own verdict entry
   * rather than the first row's.
   */
  private blockPath(): string[] {
    const segments: string[] = [];
    let node: AbstractControl = this.parentForm;
    while (node.parent) {
      const parent: AbstractControl = node.parent;
      const child = node;
      let name: string | null = null;
      if (parent instanceof FormGroup) {
        name = Object.keys(parent.controls).find(candidate => parent.controls[candidate] === child) ?? null;
      } else if (parent instanceof FormArray) {
        const index = parent.controls.indexOf(child);
        name = index >= 0 ? String(index) : null;
      }
      if (name === null) break;
      segments.unshift(name);
      node = parent;
    }
    // The SAME helper the evaluator addresses its entries with — see blockPathSegment (#490).
    segments.push(blockPathSegment(this.block));
    return segments;
  }

  private evaluateRules() {
    const wasVisible = this.isVisible;

    // The form-wide verdict wins when it has an entry for this block. When it does not — no enclosing
    // DynamicFormComponent at all, or a gap like #490 — fall back to evaluating this block's own rule rather
    // than defaulting to "visible". Defaulting was the reason a whole missing block type stayed invisible to
    // every test: a conditional block with no verdict rendered unconditionally and looked deliberate.
    const entry = this.visibility?.at(this.blockPath()) ?? null;
    this.isVisible = entry ? entry.visible : this.evaluateStandalone();

    if (wasVisible !== this.isVisible) {
      // Force change detection to ensure immediate update in UI
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  /**
   * Fallback used only when no {@link FormVisibilityState} is provided (i.e. no enclosing
   * {@link DynamicFormComponent}): evaluate this one block's own rules, including their enable/disable and
   * `requiredWhen` consequences, so a standalone story still behaves.
   *
   * Inside a real form this is dead code by design — the enable/disable/required sweep runs once for the whole
   * definition in {@link DynamicFormComponent.applyVisibility}, so it also reaches the pages that are not
   * rendered.
   */
  private evaluateStandalone(): boolean {
    const rootValue = this.buildContext();

    let visible = this.isVisible;
    if (this.block.visibleWhen) {
      visible = this.rulesEngine.evaluateRuleGroup(this.block.visibleWhen, rootValue);
    }

    if ('key' in this.block) {
      const control = this.parentForm.get([this.block.key]);
      if (control) {
        if (this.block.enabledWhen) {
          const isEnabled = this.rulesEngine.evaluateRuleGroup(this.block.enabledWhen, rootValue);
          if (isEnabled && control.disabled) control.enable({emitEvent: false});
          if (!isEnabled && control.enabled) control.disable({emitEvent: false});
        }

        // Conditionally-required fields: toggle the required validator without
        // disturbing the field's other validators. A statically-required field
        // is never un-required.
        if (this.block.requiredWhen) {
          const staticRequired = (this.block as any).required === true;
          const isRequired = staticRequired
            || this.rulesEngine.evaluateRuleGroup(this.block.requiredWhen, rootValue);
          const hasRequired = control.hasValidator(Validators.required);
          if (isRequired && !hasRequired) {
            control.addValidators(Validators.required);
            control.updateValueAndValidity({emitEvent: false});
          } else if (!isRequired && hasRequired && !staticRequired) {
            control.removeValidators(Validators.required);
            control.updateValueAndValidity({emitEvent: false});
          }
        }

        // If not visible, should also be disabled so it's not validated
        if (!visible && control.enabled) {
          control.disable({emitEvent: false});
        } else if (visible && control.disabled
          && (!this.block.enabledWhen || this.rulesEngine.evaluateRuleGroup(this.block.enabledWhen!, rootValue))) {
          control.enable({emitEvent: false});
        }
      }
    }

    return visible;
  }

  /**
   * Build the value context rules are evaluated against. The root form value is
   * the base; then each enclosing FormGroup scope (a repeatable-list row, a
   * group) overlays its own scalar fields by bare key, innermost last. This lets
   * a rule reference a sibling field "in the same row" — `field1` inside a list
   * row resolves to that row's `field1`, not a top-level one (Option A).
   *
   * {@link evaluateFormVisibility} reproduces these exact semantics from the definition + raw value; keep the
   * two in step if either is changed.
   */
  private buildContext(): any {
    const root = this.parentForm.root as FormGroup;
    const context: any = {...root.getRawValue()};

    const chain: FormGroup[] = [];
    let scope: AbstractControl | null = this.parentForm;
    while (scope) {
      if (scope instanceof FormGroup) chain.unshift(scope);
      scope = scope.parent;
    }
    for (const group of chain) {
      for (const [key, control] of Object.entries(group.controls)) {
        if (control instanceof FormControl) context[key] = control.value;
      }
    }
    return context;
  }
}
